/**
 * ForgotPasswordPage
 *
 * React component that allows users to initiate the password reset flow.
 *
 * Features
 * - Displays an input field for the user’s email address.
 * - Sends a password reset link via backend API (/auth/forgot-password).
 * - Shows loading and success/error states.
 * - Implements a visible resend timeout (30s) after submitting.
 * - Responsive layout (>300px width) using Chakra UI v3 components.
 * - Accessible input with proper <label> associations.
 *
 * Navigation:
 * “Startseite” → redirects to login page.
 * “Zum Login” and “E-Mail-Adresse korrigieren” buttons for flow control.
 *
 * Acceptance Criteria Reference
 * - Follows mockup design.
 * - Fully responsive layout.
 * - Uses Chakra UI components exclusively.
 * - Shows network loading and success/error states.
 * - Provides a resend timeout for the rate limiter.
 */

import { useState, useEffect } from 'react'
import {
  Box,
  Text,
  Heading,
  Input,
  Button,
  VStack,
  HStack,
  Container,
  chakra,
} from '@chakra-ui/react'
import { Toaster } from '@/components/ui/toaster/toaster'
import { toast } from '@/components/ui/toaster/toasterHelpers'
import { LuLockKeyhole } from 'react-icons/lu'
import { requestPasswordReset } from '@/api/auth.api'
import { useNavigate } from 'react-router-dom'
import { useFormik } from 'formik'
import {
  ForgotPasswordSchema,
  INITIAL_FORGOT_PASSWORD_VALUES,
  type ForgotPasswordData,
} from '@/common/auth.common'

export default function ForgotPasswordPage() {
  type ScreenState = 'request' | 'success'

  const [screen, setScreen] = useState<ScreenState>('request')
  const [resendTimer, setResendTimer] = useState(30)
  const navigate = useNavigate()

  // countdown for resend button
  useEffect(() => {
    if (screen === 'success' && resendTimer > 0) {
      const timer = setTimeout(() => setResendTimer(t => t - 1), 1000)
      return () => clearTimeout(timer)
    }
  }, [screen, resendTimer])

  const formik = useFormik<ForgotPasswordData>({
    initialValues: INITIAL_FORGOT_PASSWORD_VALUES,
    validationSchema: ForgotPasswordSchema,
    onSubmit: async (values, { setSubmitting }) => {
      try {
        await requestPasswordReset(values.email)
        setScreen('success')
        setResendTimer(30)
        toast.success(
          'E-Mail gesendet',
          'Ein Link zum Zurücksetzen wurde an Ihre E-Mail-Adresse gesendet.'
        )
      } catch {
        toast.error('Fehler', 'Bitte versuchen Sie es erneut.')
      } finally {
        setSubmitting(false)
      }
    },
  })

  const handleBack = () => navigate('/')
  const handleLogin = () => navigate('/login')
  const handleReset = () => setScreen('request')
  const handleResend = async () => {
    if (!formik.isValid) {
      toast.warning('Ungültige Eingabe', 'Bitte geben Sie eine gültige E-Mail-Adresse ein.')
      return
    }

    try {
      await formik.submitForm()
    } catch (err: any) {
      toast.error('Fehler', err.message || 'Bitte versuchen Sie es erneut.')
    }
  }

  return (
    <Box minH="100vh" bg="bg.subtle">
      {/* Header */}
      <HStack
        as="header"
        justify="flex-start"
        align="center"
        w="full"
        h="60px"
        px={{ base: '4', md: '6' }}
        py="3"
        gap={1}
        borderBottom="1px solid"
        borderColor="border.subtle"
        boxShadow="md"
        bg="bg.subtle"
      >
        <Button
          variant="outline"
          size="lg"
          borderRadius={'Radii/Semantic_tokens/l2'}
          colorPalette={'gray'}
          onClick={handleBack}
          borderColor={'gray.muted'}
          px={5}
          py={2}
          gap={3}
        >
          Startseite
        </Button>
      </HStack>
      {/* Main */}
      <Container
        maxW="container.lg"
        py={{ base: '10', md: '16' }}
        display="flex"
        justifyContent="center"
        alignItems="center"
      >
        {screen === 'request' ? (
          // === REQUEST SCREEN ===
          <Box width="5xl" paddingTop={32} paddingBottom={32} gap={16}>
            <Text
              textAlign="center"
              fontFamily="body"
              fontWeight="medium"
              fontSize="2xl"
              color="fg.muted"
            >
              Kennwort zurücksetzen
            </Text>

            <Container display="flex" alignItems="center" justifyContent="center">
              <chakra.form
                as="form"
                onSubmit={formik.handleSubmit}
                bg="bg"
                borderRadius="2xl"
                p="4"
                gap="4"
                width="3xl"
                border="1px solid"
                borderColor="border.subtle"
                boxShadow="md"
                shadow="sm"
                rounded="xl"
                w="full"
                maxW="768px"
                mt={16}
                display="flex"
                alignItems="center"
                justifyContent="center"
              >
                <VStack align="stretch">
                  <Text fontSize="lg" color="fg">
                    Bitte tragen Sie Ihre E-Mail-Adresse ein. Wir senden Ihnen anschließend einen
                    Link per E-Mail zu, über den Sie ein neues Kennwort vergeben können.
                  </Text>

                  {/* === Form Field === */}
                  <Box mt={1.5}>
                    <Text
                      as="label"
                      fontSize="sm"
                      fontWeight="semibold"
                      mb="1.5"
                      color="fg"
                      display="block"
                    >
                      E-Mail
                    </Text>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formik.values.email}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      variant="outline"
                      borderColor={
                        formik.touched.email && formik.errors.email ? 'red.500' : 'border.default'
                      }
                      borderWidth="1px"
                      required
                    />
                    {formik.touched.email && formik.errors.email && (
                      <Text fontSize="xs" color="fg.error" mt={1}>
                        {formik.errors.email}
                      </Text>
                    )}
                  </Box>

                  <HStack justify="flex-end" pt="2">
                    <Button
                      type="submit"
                      variant="solid"
                      bg="fg"
                      color="gray.contrast"
                      size="lg"
                      _hover={{ opacity: 0.9 }}
                      loading={formik.isSubmitting}
                      disabled={!formik.isValid || !formik.values.email}
                    >
                      Kennwort zurücksetzen <LuLockKeyhole size={16} />
                    </Button>
                  </HStack>
                </VStack>
              </chakra.form>
            </Container>
          </Box>
        ) : (
          // === SUCCESS SCREEN ===
          <Box w="full" py={32} display="flex" flexDir="column" alignItems="center" gap={2}>
            <Text textAlign="center" fontSize="2xl" fontWeight="medium" color="fg.muted">
              Kennwort zurücksetzen
            </Text>

            <Container display="flex" alignItems="center" justifyContent="center" />

            <Box
              bg="bg.default"
              borderRadius="2xl"
              p="4"
              gap="4"
              width="3xl"
              border="1px solid"
              borderColor="border.subtle"
              boxShadow="md"
              shadow="sm"
              rounded="xl"
              w="full"
              maxW="768px"
              mt={16}
              display="flex"
              alignItems="center"
              justifyContent="center"
            >
              <VStack align="stretch">
                <Heading size="lg" textAlign="left" fontWeight="600" color="fg.default">
                  Kennwort-Zurücksetzung angefordert
                </Heading>

                <Text fontSize="md" color="fg.default">
                  Wenn zu{' '}
                  <Text as="span" fontWeight="semibold">
                    ({formik.values.email})
                  </Text>{' '}
                  ein Konto besteht, senden wir Ihnen in Kürze eine E-Mail mit einem Link zum
                  Zurücksetzen. Bitte prüfen Sie auch den Spam-Ordner. Der Link ist 15 Minuten
                  gültig.
                </Text>

                <Box display="flex" alignItems="left" justifyContent="left">
                  {resendTimer > 0 ? (
                    <Button variant="plain" disabled opacity={0.5} size="md">
                      E-Mail erneut anfordern ({resendTimer}s)
                    </Button>
                  ) : (
                    <Button variant="plain" onClick={handleResend}>
                      E-Mail erneut anfordern
                    </Button>
                  )}
                </Box>

                <HStack justify="flex-start" flexWrap="wrap" mt={2}>
                  <Button
                    onClick={handleLogin}
                    variant="solid"
                    bg="gray.solid"
                    color="gray.contrast"
                    textStyle="smMedium"
                    size="md"
                    _hover={{ opacity: 0.9 }}
                  >
                    Zum Login
                  </Button>
                  <Button
                    variant="subtle"
                    onClick={handleReset}
                    bg="gray.subtle"
                    textStyle="smMedium"
                    size="md"
                  >
                    E-Mail-Adresse korrigieren
                  </Button>
                </HStack>
              </VStack>
            </Box>
          </Box>
        )}
      </Container>
    </Box>
  )
}
